import React, { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/auth/Login';
import LessonMenu from './components/LessonMenu';
import ExerciseMenu from './components/ExerciseMenu';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import Loading from './components/Loading';
import ChatWidget from './components/chat/ChatWidget';
import BBBJoinButton from './components/bbb/BBBJoinButton';
import DictionaryPopupButton from './components/dictionary/DictionaryPopupButton';
import AiTutorButton from './components/aiTutor/AiTutorButton';
import OfflineBanner from './components/shared/OfflineBanner';
import ConversationWriteExercise from './components/conversation/ConversationWriteExercise';
import ConversationListeningExercise from './components/conversation/ConversationListeningExercise';
import ConversationFillInExercise from './components/conversation/ConversationFillInExercise';
import VocabularyFillInExercise from './components/volcabulary/VocabularyFillInExercise';
import SpeakingExercise from './components/speaking/SpeakingExercise';
import WritingExercise from './components/writing/WritingExercise';
import AiVoiceConversation from './components/conversation/AiVoiceConversation';

const exerciseComponents = {
  'conversation': ConversationWriteExercise,
  'listening': ConversationListeningExercise,
  'conversation-fillin': ConversationFillInExercise,
  'vocabulary': VocabularyFillInExercise,
  'speaking': SpeakingExercise,
  'writing': WritingExercise,
  'ai-conversation': AiVoiceConversation
};

const AppContent = () => {
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedExerciseType, setSelectedExerciseType] = useState(null);
  const [selectedDialogueNumber, setSelectedDialogueNumber] = useState(1);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  // Tracks the conversation ID set when the lesson was opened (dynamic lessons only).
  // Separate from selectedConversationId so that picking a dialogue within a seeded
  // topic doesn't make ExerciseMenu think the lesson is dynamic on the way back.
  const [lessonConversationId, setLessonConversationId] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
const { user, loading } = useAuth();

  const selectLesson = (topic, options = {}) => {
    setSelectedTopic(topic);
    setSelectedExerciseType(options.exerciseType || null);
    setSelectedDialogueNumber(options.dialogueNumber || 1);
    setSelectedConversationId(options.conversationId || null);
    setLessonConversationId(options.conversationId || null);
    setShowDashboard(false);
    setShowAdmin(false);
  };

  const selectExercise = (exerciseType, options = {}) => {
    setSelectedExerciseType(exerciseType);
    setSelectedDialogueNumber(options.dialogueNumber || 1);
    setSelectedConversationId(options.conversationId || null);
  };

  const backToLessons = () => {
    setSelectedTopic(null);
    setSelectedExerciseType(null);
    setSelectedDialogueNumber(1);
    setSelectedConversationId(null);
    setLessonConversationId(null);
    setShowDashboard(false);
    setShowAdmin(false);
  };

  const backToExerciseMenu = () => {
    // Keep selectedDialogueNumber + selectedConversationId — we're still
    // inside the same (dynamic) lesson, just returning to the exercise picker.
    setSelectedExerciseType(null);
  };

  const openDashboard = () => {
    setShowDashboard(true);
    setShowAdmin(false);
    setSelectedTopic(null);
  };

  const openAdmin = () => {
    if (user && user.is_admin) {
      setShowAdmin(true);
      setShowDashboard(false);
      setSelectedTopic(null);
    }
  };

  // Show loading while checking authentication
  if (loading) {
    return <Loading />;
  }

  let content = null;

  // Show login page if not authenticated
  if (!user) {
    content = <Login />;
  } else if (showAdmin) {
    // Show admin dashboard if selected
    content = <AdminDashboard onBackToLessons={backToLessons} />;
  } else if (showDashboard) {
    // Show dashboard if selected
    content = <Dashboard onBackToLessons={backToLessons} />;
  } else if (!selectedTopic) {
    // Show lesson menu if no lesson is selected
    content = (
      <LessonMenu
        onSelectLesson={selectLesson}
        onOpenDashboard={openDashboard}
        onOpenAdmin={openAdmin}
      />
    );
  } else if (selectedTopic && !selectedExerciseType) {
    // Show exercise menu if topic selected but no exercise type
    content = (
      <ExerciseMenu
        selectedTopic={selectedTopic}
        selectedDialogueNumber={selectedDialogueNumber}
        selectedConversationId={lessonConversationId}
        onStartExercise={selectExercise}
        onBack={backToLessons}
      />
    );
  } else {
    // Show selected exercise when both topic and exercise type are selected
    const ExerciseComponent = exerciseComponents[selectedExerciseType];
    if (ExerciseComponent) {
      content = (
        <div className="min-vh-100 bg-light">
          <ExerciseComponent
            key={`${selectedTopic}-${selectedExerciseType}-${selectedDialogueNumber}`}
            topic={selectedTopic}
            dialogueNumber={selectedDialogueNumber}
            conversationId={selectedConversationId}
            onBack={backToExerciseMenu}
          />
        </div>
      );
    }
  }

  return (
    <>
      <OfflineBanner />
      {content}
      {user && <ChatWidget user={user} />}
      {user && <BBBJoinButton meetingId="my-meeting-123" userName={user.username || user.email || 'Participant'} />}
      {user && <DictionaryPopupButton />}
      {user && <AiTutorButton />}
    </>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
